import React from 'react';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import DSBox from '../../../design-system/DSBox';
import DSTypography from '../../../design-system/Typography/DSTypography';
import DSTooltip from '../../../design-system/DSTooltip';
import { ReactComponent as PlusIcon } from '../../../design-system/Icons/PlusIcon.svg';
import { CalendarEvent } from '../../../api/calendarEvent';
import {
  KLYDO_YELLOW,
  KLYDO_BLACK,
  KLYDO_WHITE,
  NATIONAL_HOLIDAY_HOVER_BACKGROUND_COLOR,
  NATIONAL_HOLIDAY_BACKGROUND_COLOR,
} from '../../../consts/colors';
import { generateObjectId } from '../../../utils';
import { isNationalHoliday } from '../../../utils/calendarEvents';

type CalendarEventWidgetItemProps = {
  calendarEvent: CalendarEvent;
};

const CalendarEventWidgetItem = ({
  calendarEvent,
}: CalendarEventWidgetItemProps) => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();

  const calendarEventDate = new Date(
    calendarEvent.date.year,
    calendarEvent.date.month - 1,
    calendarEvent.date.day,
  );
  const day = dayjs(calendarEventDate).format('DD');
  const month = dayjs(calendarEventDate).format('MMM').toUpperCase();

  const isHighlighted = isNationalHoliday(calendarEvent);
  const circleBackgroundColor = isHighlighted
    ? KLYDO_YELLOW
    : palette.action.selected;
  const circleTextColor = isHighlighted ? KLYDO_BLACK : KLYDO_WHITE;
  const containerBackground = isHighlighted
    ? NATIONAL_HOLIDAY_BACKGROUND_COLOR
    : palette.paper.elevation2;
  const hoverContainerBackground = isHighlighted
    ? NATIONAL_HOLIDAY_HOVER_BACKGROUND_COLOR
    : palette.paper.elevation10;

  const handleClick = () => {
    const createdObjectId = generateObjectId();
    navigate(`/klydo/${createdObjectId}`, {
      state: {
        calendarEventId: calendarEvent.id,
      },
    });
  };

  return (
    <DSBox
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        background: containerBackground,
        borderRadius: spacing(12.5),
        padding: ' 4px var(--3, 24px) 4px 4px',
        gap: spacing(2),
        width: '100%',
        cursor: 'pointer',
        '&:hover': {
          background: hoverContainerBackground,
          '& svg': {
            color: KLYDO_WHITE,
          },
        },
      }}
    >
      <DSBox
        sx={{
          width: spacing(7.5),
          height: spacing(7.5),
          borderRadius: '50%',
          backgroundColor: circleBackgroundColor,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <DSTypography
          variant="h5"
          sx={{
            color: circleTextColor,
          }}
        >
          {day}
        </DSTypography>
        <DSTypography
          variant="body2"
          sx={{
            color: circleTextColor,
            letterSpacing: '0.17px',
            marginTop: spacing(-1),
          }}
        >
          {month}
        </DSTypography>
      </DSBox>

      <DSTooltip title={calendarEvent.name}>
        <DSTypography
          sx={{
            color: KLYDO_WHITE,
            fontWeight: '500',
            fontSize: '16px',
            fontFamily: 'Gilroy-Medium',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {calendarEvent.name}
        </DSTypography>
      </DSTooltip>

      <DSTooltip title="Add klydo to this unique feed">
        <DSBox
          sx={{
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: palette.action.active,
          }}
        >
          <PlusIcon width={20} height={20} />
        </DSBox>
      </DSTooltip>
    </DSBox>
  );
};

export default CalendarEventWidgetItem;
